import moment from "moment";

const firstValid = (...values) =>
  values.find((value) => value != null && value !== "") ?? null;

export const getPurchasedAt = (policy) => {
  const coverageDuration = policy?.purchased_policy_data?.coverageDuration;
  return firstValid(
    policy?.purchased_at,
    policy?.purchase_date,
    policy?.purchased_on,
    policy?.date_purchased,
    policy?.created_at,
    policy?.purchased_policy_data?.purchased_at,
    policy?.purchased_policy_data?.purchase_date,
    policy?.purchased_policy_data?.created_at,
    coverageDuration?.purchased_at,
    coverageDuration?.purchase_date && coverageDuration?.purchase_time
      ? `${coverageDuration.purchase_date} ${coverageDuration.purchase_time}`
      : null,
    coverageDuration?.purchase_date
  );
};

export const getPolicyStartDate = (policy) =>
  firstValid(
    policy?.policy_start_date,
    policy?.start_date,
    policy?.effective_date,
    getPurchasedAt(policy)
  );

const momentUnitFromLabel = (unit) => {
  const normalized = String(unit || "").toLowerCase();
  if (normalized.startsWith("min")) return "minutes";
  if (normalized.startsWith("hour")) return "hours";
  if (normalized.startsWith("day")) return "days";
  if (normalized.startsWith("week")) return "weeks";
  if (normalized.startsWith("month")) return "months";
  if (normalized.startsWith("year")) return "years";
  return null;
};

const addCoverageDuration = (start, coverageDuration) => {
  if (!coverageDuration || typeof coverageDuration !== "object") return null;
  const startMoment = moment(start);
  if (!startMoment.isValid()) return null;

  if (coverageDuration.display_value != null && coverageDuration.display_unit) {
    const unit = momentUnitFromLabel(coverageDuration.display_unit);
    const value = Number(coverageDuration.display_value);
    if (unit && Number.isFinite(value)) {
      return startMoment.clone().add(value, unit).toISOString();
    }
  }

  if (coverageDuration.hours != null && coverageDuration.hours !== "") {
    const hours = Number(coverageDuration.hours);
    if (Number.isFinite(hours)) {
      return startMoment.clone().add(hours, "hours").toISOString();
    }
  }

  if (coverageDuration.days != null && coverageDuration.days !== "") {
    const days = Number(coverageDuration.days);
    if (Number.isFinite(days)) {
      return startMoment.clone().add(days, "days").toISOString();
    }
  }

  if (typeof coverageDuration.duration === "string") {
    const match = coverageDuration.duration
      .trim()
      .toLowerCase()
      .match(/^(\d+)\s*(minutes?|hours?|days?|weeks?|months?|years?)$/);
    if (match) {
      const unit = momentUnitFromLabel(match[2]);
      const value = Number(match[1]);
      if (unit && Number.isFinite(value)) {
        return startMoment.clone().add(value, unit).toISOString();
      }
    }
  }

  return null;
};

export const getPolicyExpiryDate = (policy) => {
  const explicit = firstValid(
    policy?.policy_expiry_date,
    policy?.expiry_date,
    policy?.valid_until,
    policy?.end_date
  );
  if (explicit) return explicit;

  const startDate = getPolicyStartDate(policy);
  const coverageDuration = policy?.purchased_policy_data?.coverageDuration;
  return addCoverageDuration(startDate, coverageDuration);
};

export const formatPolicyDate = (value) => {
  if (value == null || value === "") return "N/A";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM, YYYY") : "N/A";
};

export const formatPolicyTime = (value) => {
  if (value == null || value === "") return "N/A";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("hh:mm A") : "N/A";
};

export const formatValidityPeriod = (start, end) => {
  const startDate = moment(start);
  const endDate = moment(end);
  if (startDate.isValid() && endDate.isValid()) {
    const includeTime =
      startDate.isSame(endDate, "day") || endDate.diff(startDate, "hours") < 48;
    const format = includeTime ? "DD MMM, YYYY, hh:mm A" : "DD MMM, YYYY";
    return `${startDate.format(format)} – ${endDate.format(format)}`;
  }
  if (endDate.isValid()) return `Until ${endDate.format("DD MMM, YYYY")}`;
  return "N/A";
};

export const getPolicyDateInfo = (policy) => {
  const purchasedAt = getPurchasedAt(policy);
  const startDate = getPolicyStartDate(policy);
  const expiryDate = getPolicyExpiryDate(policy);

  return {
    purchasedAt,
    startDate,
    expiryDate,
    purchaseDateLabel: formatPolicyDate(purchasedAt),
    purchaseTimeLabel: formatPolicyTime(purchasedAt),
    startDateLabel: formatPolicyDate(startDate),
    validityLabel: formatValidityPeriod(startDate, expiryDate),
  };
};
